<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="success"
        icon
        v-on="on"
      >
        <v-icon dark>
          mdi-plus
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Task"
      text="Create Task"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row no-gutters>
          <v-col cols="12">
            <v-textarea
              v-model="content"
              v-validate="'required'"
              outlined
              label="Task Content"
              :rows="4"
              auto-grow
              class="mt-4"
              data-vv-name="task content"
              :error-messages="errors.collect('task content')"
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="priority"
              v-validate="'required'"
              :error-messages="errors.collect('priority')"
              data-vv-name="priority"
              label="Priority"
              item-value="id"
              item-text="name"
              :items="priorities"
              hide-no-data
              hide-selected
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="category"
              v-validate="'required'"
              :error-messages="errors.collect('category')"
              data-vv-name="category"
              label="Category"
              item-value="id"
              item-text="name"
              :items="taskCategories"
              hide-no-data
              hide-selected
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedAssignee"
              v-validate="'required'"
              :error-messages="errors.collect('assignee')"
              data-vv-name="assignee"
              label="Assignees"
              item-value="employee_uid"
              item-text="user.name"
              :loading="assigneeLoading"
              :search-input.sync="searchAssignee"
              :items="subordinates"
              multiple
              chips
              clearable
              hide-no-data
              hide-selected
              deletable-chips
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-2"
          >
            <v-autocomplete
              v-model="selectedViewer"
              :error-messages="errors.collect('viewer')"
              data-vv-name="viewer"
              label="Viewers"
              item-value="employee_uid"
              item-text="user.name"
              :items="filteredObservers"
              :loading="viewerLoading"
              :search-input.sync="searchViewer"
              multiple
              chips
              clearable
              hide-no-data
              hide-selected
              deletable-chips
            />
          </v-col>

          <v-col cols="12">
            <helper-file-input
              v-model="file" 
              :max-size-in-mb="2"
              :max-number="5"
              :multiple="true"
              data-vv-name="files"
              field-name="files"
              accept-type=".xlsx,.pdf,.docx,.jpeg,.jpg,.png"    
              extension="ext:xlsx,pdf,docx,jpeg,jpg,png"      
            >
              <div 
                slot="activator"
                class="mb-2"
              >   
                <v-btn                    
                  :disabled="progressing"
                  class="mx-2"    
                >
                  Add file
                </v-btn>
                <span> 
                  {{ file ? '' : 'No file chosen (.pdf, .docx, .xslsx, .jpeg, .jpg, .png only)' }}
                </span>
                <div
                  v-if="file"
                  class="mt-4"
                >
                  <p
                    v-for="selectedFile in file.selectedFiles"
                    :key="selectedFile.name"
                    class="mb-0"
                  >
                    <span class="font-weight-bold">{{ selectedFile.name }}</span>
                  </p>
                </div>
              </div>
            </helper-file-input>
            <span class="red--text text--lighten-1">{{ errors.first('files') }}</span>
          </v-col>

          <v-col cols="12">
            <v-menu
              v-model="date_menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="due_date"
                  v-validate="'required'"
                  :error-messages="errors.collect('due_date')"
                  data-vv-name="due_date"
                  data-vv-as="due date"
                  label="Due date"
                  prepend-icon="event"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="due_date"
                no-title
                @input="date_menu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>  
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Create
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },

    inject: ['$validator'],
    props: {
      employeeUid: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        subordinates: [],
        priorities: [],
        taskCategories: [],
        selectedAssignee: null,
        viewers: [],
        selectedViewer: null,
        content: null,
        due_date: null,
        date_menu: false,
        file: null,
        assigneeLoading: false, 
        viewerLoading: false,
        searchAssignee: null,
        searchViewer: null,
        priority: null,
        category: null,
      }
    },

    computed: {
      filteredObservers () {
        if (!this.selectedAssignee) return this.subordinates
     
        return this.subordinates.filter(subordinate => {
          return !this.selectedAssignee.includes(subordinate.employee_uid)
        })
      },
    },

    watch: {
      dialog (value) {
        if (value) {
          this.loadData()
        }
        this.resetForm()
      },

      searchAssignee (val) {
        this.subordinates.map(subordinate => {
          if (subordinate.user.name === val) return true
        })
    
        this.assigneeLoading = true
        this.fetchAssigneeEntriesDebounced(val)
      },

      searchViewer (val) {
        this.subordinates.map(subordinate => {
          if (subordinate.user.name === val) return true
        })
    
        this.viewerLoading = true
        this.fetchViewerEntriesDebounced(val)
      },
    },

    methods: {
      fetchAssigneeEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.fetchEntries(val)
        }, 500) /* 500ms throttle */
      },

      fetchViewerEntriesDebounced (val) {
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          this.fetchEntries(val)
        }, 500) /* 500ms throttle */
      },

      fetchEntries (val) {
        this.$api.employees().search({ name: val })
          .then(response => {
            this.assigneeLoading = false
            this.viewerLoading = false
            response.data.map(data => {
              this.subordinates.push({ employee_uid: data.employee.employee_uid, user: { name: data.name } })
            })
          })
      },

      loadData () {
        Promise.all([
          this.$api.subordinates().recursiveList(this.employeeUid),
          this.$api.taskPriorities().list(),
          this.$api.taskCategories().list(),
        ])
          .then(([employee, priority, taskCategory]) => {
            this.subordinates = _.orderBy(employee.data, ['user.name'], ['asc'])
            this.viewers = _.orderBy(employee.data, ['user.name'], ['asc'])

            this.priorities = priority.data
            this.taskCategories = taskCategory.data
          })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.ownerTasks().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 201) {
                  this.$emit('created')             
                  this.$snack({ message: 'Task Created' })
                  this.dialog = false    
                }
              }).catch(() => {
                this.$snack({ 
                  color: 'error',
                  message: 'Failed to create task', 
                })
              }).then(() => {
                this.progressing = false
              })
          })
      },

      prepareFormData () {
        if (this.file) {
          this.file.formData.set('assignee_uids', JSON.stringify(this.selectedAssignee))
          this.file.formData.set('viewer_uids', JSON.stringify(this.selectedViewer))
          this.file.formData.set('content', this.content)
          this.file.formData.set('due_date', this.due_date)
          this.file.formData.set('priority_id', this.priority)
          this.file.formData.set('task_category_id', this.category)

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('assignee_uids', JSON.stringify(this.selectedAssignee))
        formData.append('viewer_uids', JSON.stringify(this.selectedViewer))
        formData.append('content', this.content)
        formData.append('due_date', this.due_date)
        formData.append('priority_id', this.priority)
        formData.append('task_category_id', this.category)

        return formData
      },

      resetForm () {
        this.$validator.reset()
        this.selectedAssignee = null
        this.selectedViewer = null
        this.content = null
        this.file = null
        this.due_date = null
        this.priority = null
        this.category = null
      },
    },
  }
</script>
